import type { ReactElement } from 'react';
import { knobValue } from '@shared/page-knobs';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { ImageSlot } from '../../components/ImageSlot';
import { knobClasses } from './knobs';
import { Frame, focalPointStyle } from './primitives';
import { useBindings } from '../../state/BindingsContext';

/**
 * Joins up to 2 client-safe principles into a short support line (docs/
 * momentum-v3-slides.md "strategy": "support = fields.support else first two
 * principles"). Each principle is already ≤12 words (src/domain/types.ts
 * `MediaStrategy.principles`), so this only punctuates and concatenates - it
 * never rewrites the words.
 */
function supportFromPrinciples(principles: string[] | null | undefined): string | null {
  const list = (principles ?? []).map((p) => p.trim()).filter(Boolean).slice(0, 2);
  if (list.length === 0) return null;
  return list.map((p) => (/[.!?]$/.test(p) ? p : `${p}.`)).join(' ');
}

/**
 * strategy (docs/momentum-v3-slides.md "strategy"): STATEMENT dark, the
 * single authoritative campaign thesis. `content.body` wins over
 * `strategy.summary` when the rep has written one - the same precedence
 * momentum_v1's `StrategyPage` uses; `fields.support` wins over the
 * principles join the same way.
 *
 * Rebuilt as a full-bleed photography moment (contract: "Full-bleed Pexels
 * image + angled dark gradient (~105°, ~96% dark at left to ~20% at right).
 * No image → solid dark surface") in place of v2's flat dark card with a
 * "Strategy kernel" pill, eyebrow, mint rule and cards - all removed. The
 * thesis drops one step from v2's display size (96px → 64px, brief's h1) so
 * an 80-character sentence comfortably fits two lines. The hero uses the
 * same hidden-`ImageSlot`-over-a-manual-`<img>` affordance pattern as
 * `CoverPage`, so a rep can add or replace the photo whether or not one is
 * already set.
 */
export function StrategyPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  // Agent copy carries live bindings ({{advertiser.name}}); resolve them here
  // because the statement and support line render as plain h1/p, not Editable.
  const resolve = useBindings();
  const rawStatement = section.content.body || proposal.strategy.summary || '';
  const rawSupport = section.content.fields?.support || supportFromPrinciples(proposal.strategy.principles) || '';
  const statement = rawStatement ? resolve(rawStatement) : undefined;
  const support = rawSupport ? resolve(rawSupport) : null;
  const imageUrl = section.content.imageUrl;
  // supportLine (docs/composer-cards.md §13f "StrategyPages"): "off" drops
  // the line entirely, same as there being no support copy at all - a plain
  // CSS hide would leave it in the DOM for no reason once it's a rep choice
  // rather than a data gap.
  const showSupport = support && knobValue('strategy', section.style, 'supportLine') !== 'off';

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-kernel-root', ...knobClasses(section)].join(' ')}
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-kernel-media">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              aria-hidden="true"
              className="mv3-kernel-image"
              style={focalPointStyle(section)}
            />
          ) : null}
          <div className="mv3-kernel-gradient" aria-hidden="true" />
          {!readOnly ? (
            <ImageSlot
              url={imageUrl}
              alt="Strategy image"
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { imageUrl: next })}
              className="mv3-kernel-image-affordance"
              hideImage
              proposalId={proposal.proposalId}
              defaultQuery={proposal.advertiser.industry ?? section.title}
            />
          ) : null}
        </div>
        <div className="mv3-kernel-inner">
          <h1 className="mv3-kernel-statement">{statement || section.title}</h1>
          {showSupport ? <p className="mv3-kernel-support">{support}</p> : null}
        </div>
      </Frame>
    </PageShell>
  );
}
